import React, { useState } from 'react';
import { 
  BookOpen, 
  Play, 
  Clock, 
  Users, 
  Star, 
  Search,
  Filter,
  ChevronRight,
  TrendingUp,
  DollarSign,
  BarChart3,
  Globe
} from 'lucide-react';

interface LearningResource {
  id: string;
  title: string;
  description: string;
  type: 'article' | 'video' | 'course';
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  duration: string;
  rating: number;
  author: string;
  category: string;
  thumbnail?: string;
}

const Learning: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');

  const categories = [
    { id: 'all', name: 'All Categories', icon: Globe },
    { id: 'macroeconomics', name: 'Macroeconomics', icon: TrendingUp },
    { id: 'microeconomics', name: 'Microeconomics', icon: DollarSign },
    { id: 'econometrics', name: 'Econometrics', icon: BarChart3 },
    { id: 'monetary-policy', name: 'Monetary Policy', icon: BookOpen },
  ];

  const mockResources: LearningResource[] = [
    {
      id: '1',
      title: 'Understanding GDP and Economic Growth',
      description: 'A comprehensive guide to Gross Domestic Product, its components, and how it measures economic performance.',
      type: 'article',
      difficulty: 'Beginner',
      duration: '15 min read',
      rating: 4.8,
      author: 'Dr. Sarah Johnson',
      category: 'macroeconomics'
    },
    {
      id: '2',
      title: 'Introduction to Supply and Demand',
      description: 'Learn the fundamental concepts of supply and demand and how they determine market prices.',
      type: 'video',
      difficulty: 'Beginner',
      duration: '25 min watch',
      rating: 4.9,
      author: 'Prof. Michael Chen',
      category: 'microeconomics'
    },
    {
      id: '3',
      title: 'Monetary Policy Tools and Their Effects',
      description: 'Explore how central banks use interest rates, reserve requirements, and quantitative easing.',
      type: 'course',
      difficulty: 'Intermediate',
      duration: '2 hours',
      rating: 4.7,
      author: 'Federal Reserve Education',
      category: 'monetary-policy'
    },
    {
      id: '4',
      title: 'Regression Analysis in Economics',
      description: 'Master the statistical techniques used to analyze economic relationships and forecast trends.',
      type: 'course',
      difficulty: 'Advanced',
      duration: '4 hours',
      rating: 4.6,
      author: 'Dr. Emily Rodriguez',
      category: 'econometrics'
    },
    {
      id: '5',
      title: 'Inflation: Causes and Consequences',
      description: 'Understand what drives inflation, how it affects the economy, and policy responses.',
      type: 'article',
      difficulty: 'Intermediate',
      duration: '20 min read',
      rating: 4.8,
      author: 'Economic Policy Institute',
      category: 'macroeconomics'
    },
    {
      id: '6',
      title: 'Market Structures and Competition',
      description: 'Compare perfect competition, monopoly, oligopoly, and monopolistic competition.',
      type: 'video',
      difficulty: 'Intermediate',
      duration: '35 min watch',
      rating: 4.7,
      author: 'Khan Academy',
      category: 'microeconomics'
    }
  ];

  const filteredResources = mockResources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'all' || resource.difficulty === selectedDifficulty;
    
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Play className="h-4 w-4" />;
      case 'course':
        return <BookOpen className="h-4 w-4" />;
      default:
        return <BookOpen className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'video':
        return 'bg-red-100 text-red-800';
      case 'course':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-100 text-green-800';
      case 'Intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'Advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Economics Learning Portal</h1>
        <p className="mt-2 text-gray-600">
          Master economic concepts through articles, videos, and interactive courses
        </p>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search learning resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
            
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Levels</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
            </select>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {categories.map(category => {
          const Icon = category.icon;
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`p-4 rounded-lg border-2 transition-colors ${
                selectedCategory === category.id
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <Icon className="h-6 w-6 mx-auto mb-2" />
              <p className="text-sm font-medium text-center">{category.name}</p>
            </button>
          );
        })}
      </div>

      {/* Learning Resources */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Learning Resources ({filteredResources.length})
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResources.map(resource => (
            <div key={resource.id} className="card hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(resource.type)}`}>
                    {getTypeIcon(resource.type)}
                    <span className="ml-1 capitalize">{resource.type}</span>
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(resource.difficulty)}`}>
                    {resource.difficulty}
                  </span>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{resource.title}</h3>
              <p className="text-gray-600 text-sm mb-4 line-clamp-3">{resource.description}</p>
              
              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {resource.duration}
                  </div>
                  <div className="flex items-center">
                    <Star className="h-4 w-4 mr-1 text-yellow-400 fill-current" />
                    {resource.rating}
                  </div>
                </div>
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-1" />
                  {resource.author}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Featured Learning Paths */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Featured Learning Paths</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <TrendingUp className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Macroeconomics Fundamentals</h3>
                <p className="text-sm text-gray-600">6 courses • 12 hours</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              Master the basics of macroeconomic theory, from GDP and inflation to monetary and fiscal policy.
            </p>
            <button className="btn-primary w-full">Start Learning Path</button>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <BarChart3 className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Economic Data Analysis</h3>
                <p className="text-sm text-gray-600">4 courses • 8 hours</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              Learn to analyze economic data using statistical tools and econometric methods.
            </p>
            <button className="btn-primary w-full">Start Learning Path</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Learning; 